import React, { createContext, SetStateAction, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { usePost } from '../API';
import * as v0 from '@aidkitorg/types/lib/survey';
import { logAdminPanelError } from '../utils/adminPanel.utils';
import { 
  Dashboard, 
  DashboardComponentWithId, 
  StagedComponent, 
  SupportedDashboardComponent, 
  TranslatedComponentResult
} from '@aidkitorg/types/lib/admin-panel/dashboard';
import { FilterConfig } from '@aidkitorg/types/lib/admin-panel/filters';
import { v4 } from 'uuid';
import { Layout } from 'react-grid-layout';

// Cache types
type CacheKey = string;
type CacheEntry = {
  data: DashboardWithComponents;
  timestamp: number;
  ttl: number;
};
type DashboardCache = Map<CacheKey, CacheEntry>;

const CACHE_MAX_ENTRIES = 10;
const CACHE_TTL_MS = 600000; // 10 minute TTL for dashboards

export type DashboardWithComponents = Dashboard & {
  lastUpdated: number,
  components: Array<DashboardComponentWithId>
}

export type HeroNumberWithFields = v0.HeroNumberWithFields & {description?: string}
export type BarChart = v0.BarChart & {description?: string}
export type PieChart = v0.PieChart & {description?: string}

type DashboardComponentTranslationReturnType = {
  success: boolean,
  data?: TranslatedComponentResult,
  errorCode?: number,
}

type DashboardsContextType = {
  dashboards: Array<Dashboard>
  dashboardsById: DashboardsById,
  getDashboards: () => void
  isDashboardsLoading: boolean,  

  fetchDashboardById: (id: string, forceRefresh?: boolean) => void,
  getDashboardById: (id: string) => DashboardWithComponents | void,

  deleteDashboardById: (id: string) => void,

  // Dashboard Builder
  stagedComponents: Array<StagedComponent | DashboardComponentWithId>,
  setStagedComponents: React.Dispatch<SetStateAction<Array<StagedComponent | DashboardComponentWithId>>>,

  createDashboard: (title: string, description: string, components: Array<StagedComponent>, layout: Array<Layout>) => Promise<string | void>,
  updateDashboard: (id: string, title: string, description: string, components: Array<StagedComponent>, layout: Array<Layout>) => Promise<string | void>,
  getDashboardComponentTranslation: (component: SupportedDashboardComponent, viewFilters?: FilterConfig) => Promise<DashboardComponentTranslationReturnType | void>

  logDashboardDownload: (id: string) => Promise<boolean>
}

type DashboardsById = Record<string, DashboardWithComponents>

type DashboardsProviderProps = {
  children: React.ReactNode
}

const DashboardsContext = createContext<DashboardsContextType>({
  dashboards: [],
  dashboardsById: {},
  getDashboards: async () => {},
  
  isDashboardsLoading: false,

  fetchDashboardById: async () => {},
  getDashboardById: () => {},

  deleteDashboardById: async () => {},

  stagedComponents: [],
  setStagedComponents: (_prev) => {},

  createDashboard: async (_title, _desc, _components, _layout) => {},
  updateDashboard: async (_id, _title, _desc, _components, _layout) => {},
  getDashboardComponentTranslation: async (_component: SupportedDashboardComponent, _viewFilters?: FilterConfig) => {},

  logDashboardDownload: async (_id) => { return true },
})

export const useDashboards = () => {
  const context = useContext(DashboardsContext)
  if (!context) {
    throw new Error("useDashboards must be used within a DashboardsProvider")
  }

  return context;
}

export const DashboardsProvider: React.FC<DashboardsProviderProps> = ({ children }) => {
  // TODO: update this array type
  const [dashboards, setDashboards] = useState<Array<Dashboard>>([])
  const [isDashboardsLoading, setIsDashboardsLoading] = useState(false)

  const [stagedComponents, setStagedComponents] = useState<Array<StagedComponent>>([])

  const [dashboardsById, setDashboardsById] = useState<DashboardsById>({})

  // Dashboard cache
  const dashboardCacheRef = useRef<DashboardCache>(new Map())

  // Hash function for cache keys (similar to Reports)
  const hashString = useCallback((str: string): string => {
    let hash = 0;
    if (str.length === 0) return hash.toString();
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash).toString(36);
  }, []);

  // Cache utility functions
  const generateCacheKey = useCallback((dashboardId: string) => {
    return hashString(`dashboard-${dashboardId}`);
  }, [hashString]);

  const getCachedDashboard = useCallback((cacheKey: string): DashboardWithComponents | null => {
    const cache = dashboardCacheRef.current;
    const entry = cache.get(cacheKey);
    
    if (!entry) return null;
    
    const now = Date.now();
    if (now > entry.timestamp + entry.ttl) {
      console.log("delete cached result")
      cache.delete(cacheKey);
      return null;
    }
    
    return entry.data;
  }, []);

  const setCachedDashboard = useCallback((cacheKey: string, data: DashboardWithComponents, ttlMs: number = CACHE_TTL_MS) => {
    const cache = dashboardCacheRef.current;
        
    // Lazy cleanup: remove expired entries when cache gets too big
    if (cache.size >= CACHE_MAX_ENTRIES) {
      const now = Date.now();
      const entries = Array.from(cache.entries());
      
      // Remove expired entries
      for (const [key, entry] of entries) {
        if (now > entry.timestamp + entry.ttl) {
          cache.delete(key);
        }
      }
      
      // If still too big after cleanup, remove oldest entry
      if (cache.size >= CACHE_MAX_ENTRIES) {
        const remainingEntries = Array.from(cache.entries());
        remainingEntries.sort((a, b) => a[1].timestamp - b[1].timestamp);
        cache.delete(remainingEntries[0][0]);
      }
    }
    
    cache.set(cacheKey, {
      data,
      timestamp: Date.now(),
      ttl: ttlMs
    });
  }, []);

  // Cleanup cache on unmount
  useEffect(() => {
    return () => {
      dashboardCacheRef.current.clear();
    };
  }, []);

  const getAdminPanelDashboards = usePost("/api/admin_panel/dashboards")
  const fetchDashboardByIdPost = usePost("/api/admin_panel/dashboard")
  const translateDashboardComponent = usePost("/api/admin_panel/dashboards/component/translate")
  const createDashboardPost = usePost("/api/admin_panel/dashboards/create")
  const updateDashboardPost = usePost("/api/admin_panel/dashboards/update")
  const deleteDashboard = usePost("/api/admin_panel/dashboards/delete")
  const logDashboardDownloadPost = usePost("/api/admin_panel/dashboard/log_download")

  const getDashboards = async () => {
    setIsDashboardsLoading(true)
    const result = await getAdminPanelDashboards({})
    setIsDashboardsLoading(false)

    if (result.success && result.data) {
      setDashboards(result.data.dashboards)
    } else if (result.error) {
      logAdminPanelError(result.error, result.message)
    }
  }

  const fetchDashboardById = async (id: string, forceRefresh: boolean = false) => {
    // Check cache first (unless force refresh)
    console.log(forceRefresh)
    if (!forceRefresh) {
      const cacheKey = generateCacheKey(id);
      const cachedDashboard = getCachedDashboard(cacheKey);
      
      if (cachedDashboard) {
        // Update state with cached data
        setDashboardsById((prev) => ({
          ...prev,
          [id]: cachedDashboard
        }));
        return;
      }
    }

    const result = await fetchDashboardByIdPost({dashboardId: id, forceRefresh})

    if (result.success && result.data) {
      const dashboard = result.data
      // Transform legacy API response to match new DashboardComponentWithId structure
      const componentsWithIds: DashboardComponentWithId[] = dashboard.components
      
      const dashboardWithComponents: DashboardWithComponents = {
        id: dashboard.id,
        title: dashboard.title,
        description: dashboard.description,
        created_by: dashboard.created_by,
        created_at: dashboard.created_at,
        updated_at: dashboard.updated_at,
        view_only: dashboard.view_only,
        components: componentsWithIds,
        lastUpdated: dashboard.lastUpdated,
        source: dashboard.source,
        href: dashboard.href
      }

      // Cache the dashboard
      const cacheKey = generateCacheKey(id);
      setCachedDashboard(cacheKey, dashboardWithComponents);
      
      setDashboardsById((prev) => (
        {...prev,
          [dashboard.id]: dashboardWithComponents
        }
      ))
    } else if (result.error) {
      logAdminPanelError(result.error, result.message)
    }
  }

  const getDashboardById = (id: string) => {
    return dashboardsById[id]
  }

  const deleteDashboardById = async (id: string) => {
    const result = await deleteDashboard({dashboardId: id})

    if (result.success) {
      // Remove from both state variables
      console.log("deleted ", id)
      setDashboards(dashboards.filter((dashboard) => dashboard.id !== id))
      setDashboardsById(prev => {
        const newState = { ...prev }
        delete newState[id]
        return newState
      })

      // Invalidate cache
      const cacheKey = generateCacheKey(id);
      dashboardCacheRef.current.delete(cacheKey);
    }
  }

  const getDashboardComponentTranslation = async (component: SupportedDashboardComponent, viewFilters?: FilterConfig) => {
    const result = await translateDashboardComponent({
      component: {
        // Generates a temporary unique identifier for this translation request
        id: `temp-${v4()}`,
        config: component
      },
      viewFilters
    })

    if (result.success) {
      return { success: true, data: result.data?.result}
    } else if (result.error) {
      logAdminPanelError(result.error, result.message)
      return { success: false, errorCode: result.error.code }
    }
  }

  const createDashboard = async (title: string, description: string, components: Array<StagedComponent>, layout: Array<Layout>) => {
    const result = await createDashboardPost({
      title: title,
      description: description,
      components: components.map(sc => ({
        id: sc.id,
        config: sc.config,
        style: layout.find(l => l.i === sc.id) || {},
      }))
    })

    if (result.success && result.data) {
      return result.data.id;
    } else if (result.error) {
      logAdminPanelError(result.error, result.message)
    }
  }

  const updateDashboard = async (id: string, title: string, description: string, components: Array<StagedComponent>, layout: Array<Layout>) => {
    const result = await updateDashboardPost({
      dashboardId: id,
      title: title,
      description: description,
      components: components.map(sc => ({
        id: sc.id,
        config: sc.config,
        style: layout.find(l => l.i === sc.id) || {},
      }))
    })

    if (result.success && result.data) {
      // Invalidate cache for updated dashboard
      const cacheKey = generateCacheKey(id);
      dashboardCacheRef.current.delete(cacheKey);
      
      return result.data.id;
    } else if (result.error) {
      logAdminPanelError(result.error, result.message)
    }
  }

  const logDashboardDownload = async (id: string) => {
    const result = await logDashboardDownloadPost({
      dashboardId: id,
    })

    if (result.error) {
      logAdminPanelError(result.error, result.message)
      return false
    }

    return true
  }

  const value = {
    dashboards,
    dashboardsById,
    getDashboards, 
    isDashboardsLoading,

    fetchDashboardById,
    getDashboardById,
    deleteDashboardById,

    stagedComponents,
    setStagedComponents,

    createDashboard,
    updateDashboard,
    getDashboardComponentTranslation,

    logDashboardDownload
  }
    
  return (
    <DashboardsContext.Provider value={value}>
      {children}
    </DashboardsContext.Provider>
  )
}